import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Plus, Calendar, Clock, Play, Edit, Trash2,
  Pause, CheckCircle, AlertCircle,
  Timer, RefreshCw
} from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCHEDULE_TYPES = [
  { value: "once", label: "Run Once" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" }
];

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" }
];

export default function Schedules() {
  const navigate = useNavigate();
  const [confirm, confirmDialog] = useConfirm();
  const [schedules, setSchedules] = useState([]);
  const [suites, setSuites] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    suite_id: "",
    test_case_ids: [],
    schedule_type: "daily",
    schedule_time: "09:00",
    schedule_days: [0, 1, 2, 3, 4], // Mon-Fri by default
    timezone: "UTC",
    is_active: true,
    notify_on_failure: true,
    notify_emails: []
  });
  const [emailInput, setEmailInput] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, suitesRes, casesRes] = await Promise.all([
        axios.get(`${API}/schedules`, { withCredentials: true }),
        axios.get(`${API}/suites`, { withCredentials: true }),
        axios.get(`${API}/test-cases`, { withCredentials: true })
      ]);
      setSchedules(schedulesRes.data);
      setSuites(suitesRes.data);
      setTestCases(casesRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load schedules");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      suite_id: "",
      test_case_ids: [],
      schedule_type: "daily",
      schedule_time: "09:00",
      schedule_days: [0, 1, 2, 3, 4],
      timezone: "UTC",
      is_active: true,
      notify_on_failure: true,
      notify_emails: []
    });
    setEmailInput("");
  };

  const handleCreateSchedule = async () => {
    if (!formData.name.trim()) {
      toast.error("Schedule name is required");
      return;
    }
    if (!formData.suite_id && formData.test_case_ids.length === 0) {
      toast.error("Select a suite or at least one test case");
      return;
    }
    try {
      await axios.post(`${API}/schedules`, formData, { withCredentials: true });
      toast.success("Schedule created!");
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to create schedule:", error);
      toast.error(error.response?.data?.detail || "Failed to create schedule");
    }
  };

  const handleUpdateSchedule = async () => {
    if (!selectedSchedule) return;
    try {
      await axios.put(`${API}/schedules/${selectedSchedule.id}`, formData, { withCredentials: true });
      toast.success("Schedule updated!");
      setShowEditModal(false);
      setSelectedSchedule(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to update schedule:", error);
      toast.error("Failed to update schedule");
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    const ok = await confirm({
      title: 'Delete this schedule?',
      description: 'The scheduled run will be cancelled and removed. Existing execution history is kept.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`, { withCredentials: true });
      toast.success("Schedule deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete schedule:", error);
      toast.error("Failed to delete schedule");
    }
  };

  const toggleScheduleActive = async (schedule) => {
    try {
      await axios.put(`${API}/schedules/${schedule.id}`, {
        ...schedule,
        is_active: !schedule.is_active
      }, { withCredentials: true });
      toast.success(schedule.is_active ? "Schedule paused" : "Schedule activated");
      fetchData();
    } catch (error) {
      console.error("Failed to toggle schedule:", error);
      toast.error("Failed to update schedule");
    }
  };

  const openEditModal = (schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      name: schedule.name,
      suite_id: schedule.suite_id || "",
      test_case_ids: schedule.test_case_ids || [],
      schedule_type: schedule.schedule_type || "daily",
      schedule_time: schedule.schedule_time || "09:00",
      schedule_days: schedule.schedule_days || [0, 1, 2, 3, 4],
      timezone: schedule.timezone || "UTC",
      is_active: schedule.is_active !== false,
      notify_on_failure: schedule.notify_on_failure !== false,
      notify_emails: schedule.notify_emails || []
    });
    setShowEditModal(true);
  };

  const toggleDaySelection = (day) => {
    setFormData(prev => ({
      ...prev,
      schedule_days: prev.schedule_days.includes(day)
        ? prev.schedule_days.filter(d => d !== day)
        : [...prev.schedule_days, day].sort()
    }));
  };

  const toggleTestCaseSelection = (testCaseId) => {
    setFormData(prev => ({
      ...prev,
      test_case_ids: prev.test_case_ids.includes(testCaseId)
        ? prev.test_case_ids.filter(id => id !== testCaseId)
        : [...prev.test_case_ids, testCaseId]
    }));
  };

  const addEmail = () => {
    if (emailInput.trim() && !formData.notify_emails.includes(emailInput.trim())) {
      setFormData(prev => ({
        ...prev,
        notify_emails: [...prev.notify_emails, emailInput.trim()]
      }));
      setEmailInput("");
    }
  };

  const removeEmail = (email) => {
    setFormData(prev => ({
      ...prev,
      notify_emails: prev.notify_emails.filter(e => e !== email)
    }));
  };

  const getSuiteName = (suiteId) => {
    const suite = suites.find(s => s.id === suiteId);
    return suite?.name || "Unknown Suite";
  };

  const formatNextRun = (nextRun) => {
    if (!nextRun) return "Not scheduled";
    return new Date(nextRun).toLocaleString();
  };

  const getScheduleDescription = (schedule) => {
    if (schedule.schedule_type === "once") return "One-time run";
    if (schedule.schedule_type === "daily") return `Daily at ${schedule.schedule_time}`;
    if (schedule.schedule_type === "weekly") {
      const days = schedule.schedule_days?.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label.slice(0, 3)).join(", ");
      return `Weekly on ${days} at ${schedule.schedule_time}`;
    }
    if (schedule.schedule_type === "monthly") return `Monthly at ${schedule.schedule_time}`;
    return schedule.schedule_type;
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="schedules-page">
      {confirmDialog}
      <div className="fixed inset-0 hero-glow" />
      <div className="fixed inset-0 opacity-5" 
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"-%3E%3Cg fill="none" fill-rule="evenodd"-%3E%3Cg fill="%23ffffff" fill-opacity="0.4"-%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}
      />

      <div className="relative z-10">
        <AppNav />

        {/* Main Content */}
        <div className="px-6 py-12 md:px-12">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-12">
              <div>
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Scheduled Runs
                </h1>
                <p className="text-slate-400">Automate test execution on a schedule</p>
              </div>
              <Button 
                data-testid="create-schedule-btn"
                className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold neon-glow"
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Schedule
              </Button>
            </div>

            {/* Schedules List */}
            {loading ? (
              <p className="text-slate-400">Loading...</p>
            ) : schedules.length === 0 ? (
              <Card className="glass-card border-white/5 rounded-xl">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No schedules yet</h3>
                  <p className="text-slate-400 mb-6">Create a schedule to run tests automatically</p>
                  <Button 
                    className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                    onClick={() => {
                      resetForm();
                      setShowCreateModal(true);
                    }}
                  >
                    Create Schedule
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {schedules.map((schedule, idx) => (
                  <motion.div
                    key={schedule.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card 
                      className="glass-card border-white/5 rounded-xl hover:border-white/10 transition-colors"
                      data-testid={`schedule-${schedule.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                              schedule.is_active ? 'bg-[#00E599]/10' : 'bg-slate-800'
                            }`}>
                              {schedule.is_active ? (
                                <Timer className="w-6 h-6 text-[#00E599]" />
                              ) : (
                                <Pause className="w-6 h-6 text-slate-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{schedule.name}</h3>
                                <Badge className={schedule.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-slate-800 text-slate-400 border-slate-700'}>
                                  {schedule.is_active ? 'Active' : 'Paused'}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-400">{getScheduleDescription(schedule)}</p>
                              {schedule.suite_id && (
                                <p className="text-xs text-slate-500 mt-1">Suite: {getSuiteName(schedule.suite_id)}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-500">Next run</p>
                              <p className="text-sm text-slate-300">{formatNextRun(schedule.next_run)}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button 
                              data-testid={`toggle-schedule-btn-${schedule.id}`}
                              size="sm"
                              variant="ghost"
                              className={schedule.is_active ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10' : 'text-green-400 hover:text-green-300 hover:bg-green-500/10'}
                              onClick={() => toggleScheduleActive(schedule)}
                            >
                              {schedule.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            </Button>
                            <Button 
                              data-testid={`edit-schedule-btn-${schedule.id}`}
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
                              onClick={() => openEditModal(schedule)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              data-testid={`delete-schedule-btn-${schedule.id}`}
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm"
                              onClick={() => handleDeleteSchedule(schedule.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Schedule Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl bg-[#0B0F1A] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Create Schedule
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Set up automated test runs
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label>Schedule Name *</Label>
              <Input
                data-testid="schedule-name-input"
                placeholder="e.g., Daily Smoke Tests"
                className="bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Suite Selection */}
            <div className="space-y-2">
              <Label>Test Suite (Optional)</Label>
              <Select 
                value={formData.suite_id || "none"} 
                onValueChange={(value) => setFormData({ ...formData, suite_id: value === "none" ? "" : value, test_case_ids: [] })}
              >
                <SelectTrigger className="bg-[#0B0F1A] border-white/10 text-white rounded-sm">
                  <SelectValue placeholder="Select a suite" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F1A] border-white/10">
                  <SelectItem value="none">None (select individual tests)</SelectItem>
                  {suites.map(suite => (
                    <SelectItem key={suite.id} value={suite.id}>{suite.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Individual Test Cases (if no suite selected) */}
            {!formData.suite_id && (
              <div className="space-y-2">
                <Label>Select Test Cases</Label>
                <ScrollArea className="h-[150px] border border-white/10 rounded-sm p-2">
                  {testCases.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No test cases available</p>
                  ) : (
                    <div className="space-y-2">
                      {testCases.map((tc) => (
                        <div
                          key={tc.id}
                          className={`flex items-center gap-3 p-2 rounded-sm cursor-pointer transition-colors ${
                            formData.test_case_ids.includes(tc.id)
                              ? 'bg-[#00E599]/10 border border-[#00E599]/30'
                              : 'bg-white/5 hover:bg-white/10'
                          }`}
                          onClick={() => toggleTestCaseSelection(tc.id)}
                        >
                          <Checkbox 
                            checked={formData.test_case_ids.includes(tc.id)}
                            className="data-[state=checked]:bg-[#00E599] data-[state=checked]:border-[#00E599]"
                          />
                          <span className="text-sm truncate">{tc.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

            {/* Schedule Type */}
            <div className="space-y-2">
              <Label>Schedule Type</Label>
              <Select 
                value={formData.schedule_type} 
                onValueChange={(value) => setFormData({ ...formData, schedule_type: value })}
              >
                <SelectTrigger className="bg-[#0B0F1A] border-white/10 text-white rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F1A] border-white/10">
                  {SCHEDULE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time */}
            <div className="space-y-2">
              <Label>Run Time</Label>
              <Input
                type="time"
                className="bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                value={formData.schedule_time}
                onChange={(e) => setFormData({ ...formData, schedule_time: e.target.value })}
              />
            </div>

            {/* Days of week (for weekly) */}
            {formData.schedule_type === "weekly" && (
              <div className="space-y-2">
                <Label>Days to Run</Label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map(day => (
                    <Button
                      key={day.value}
                      type="button"
                      size="sm"
                      variant={formData.schedule_days.includes(day.value) ? "default" : "outline"}
                      className={formData.schedule_days.includes(day.value) 
                        ? "bg-[#00E599] text-black hover:bg-[#00CC88]" 
                        : "border-white/10 text-slate-400 hover:bg-white/5"}
                      onClick={() => toggleDaySelection(day.value)}
                    >
                      {day.label.slice(0, 3)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Notifications */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Notify on Failure</Label>
                  <p className="text-xs text-slate-500">Receive email alerts when tests fail</p>
                </div>
                <Switch
                  checked={formData.notify_on_failure}
                  onCheckedChange={(checked) => setFormData({ ...formData, notify_on_failure: checked })}
                />
              </div>
              
              {formData.notify_on_failure && (
                <div className="space-y-2">
                  <Label>Notification Emails</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      className="bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                    />
                    <Button 
                      type="button"
                      variant="outline"
                      className="border-white/10 text-white hover:bg-white/5"
                      onClick={addEmail}
                    >
                      Add
                    </Button>
                  </div>
                  {formData.notify_emails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.notify_emails.map((email, i) => (
                        <Badge key={i} className="bg-slate-800 text-slate-300">
                          {email}
                          <button onClick={() => removeEmail(email)} className="ml-1 hover:text-white">×</button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="create-schedule-submit-btn"
                className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                onClick={handleCreateSchedule}
              >
                Create Schedule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Schedule Modal - Similar structure to Create */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl bg-[#0B0F1A] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Edit Schedule
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update schedule configuration
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label>Schedule Name *</Label>
              <Input
                data-testid="edit-schedule-name-input"
                placeholder="e.g., Daily Smoke Tests"
                className="bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-slate-500">Enable or disable this schedule</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            {/* Suite Selection */}
            <div className="space-y-2">
              <Label>Test Suite (Optional)</Label>
              <Select 
                value={formData.suite_id || "none"} 
                onValueChange={(value) => setFormData({ ...formData, suite_id: value === "none" ? "" : value, test_case_ids: [] })}
              >
                <SelectTrigger className="bg-[#0B0F1A] border-white/10 text-white rounded-sm">
                  <SelectValue placeholder="Select a suite" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F1A] border-white/10">
                  <SelectItem value="none">None (select individual tests)</SelectItem>
                  {suites.map(suite => (
                    <SelectItem key={suite.id} value={suite.id}>{suite.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Schedule Type */}
            <div className="space-y-2">
              <Label>Schedule Type</Label>
              <Select 
                value={formData.schedule_type} 
                onValueChange={(value) => setFormData({ ...formData, schedule_type: value })}
              >
                <SelectTrigger className="bg-[#0B0F1A] border-white/10 text-white rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F1A] border-white/10">
                  {SCHEDULE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time */}
            <div className="space-y-2">
              <Label>Run Time</Label>
              <Input
                type="time"
                className="bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                value={formData.schedule_time}
                onChange={(e) => setFormData({ ...formData, schedule_time: e.target.value })}
              />
            </div>

            {/* Days of week (for weekly) */}
            {formData.schedule_type === "weekly" && (
              <div className="space-y-2">
                <Label>Days to Run</Label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map(day => (
                    <Button
                      key={day.value}
                      type="button"
                      size="sm"
                      variant={formData.schedule_days.includes(day.value) ? "default" : "outline"}
                      className={formData.schedule_days.includes(day.value) 
                        ? "bg-[#00E599] text-black hover:bg-[#00CC88]" 
                        : "border-white/10 text-slate-400 hover:bg-white/5"}
                      onClick={() => toggleDaySelection(day.value)}
                    >
                      {day.label.slice(0, 3)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="edit-schedule-submit-btn"
                className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                onClick={handleUpdateSchedule}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
